import React from "react";
import { Box, Typography } from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const gold = "#C29D59";
const darkBg = "#111";

const InteriorSection = () => {
  return (
    <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 4, md: 6 }, bgcolor: darkBg }}>
      {/* Heading Section */}
      <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: gold,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 2,
          }}
        >
          Why Choose Us
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: "#fff",
            mb: 2,
            fontSize: { xs: "30px", sm: "38px", md: "45px" },
            lineHeight: 1.3,
          }}
        >
          What Makes{" "}
          <Box component="span" sx={{ color: gold }}>
            Us Different
          </Box>
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: 700,
            mx: "auto",
            mb: { xs: 5, md: 8 },
            color: "#b0b0b0ff",
            lineHeight: 1.7,
            px: { xs: 1, sm: 0 },
          }}
        >
          We combine creativity, expertise, and dedication to deliver exceptional
          interior design experiences that exceed expectations.
        </Typography>
      </Box>

      {/* Main Layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 3, md: 4 },
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        {/* Left Section - 3 Cards */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "1 1 60%" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 2 },
          }}
        >
          {/* Top Two Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 3, sm: 3 },
            }}
          >
            {/* Card 1 */}
            <Box
              sx={{
                flex: 1,
                background: "linear-gradient(135deg, #e7d8c9, #fff)",
                borderRadius: "20px",
                p: { xs: 3, sm: 3.5 },
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  backgroundColor: gold,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  mx: { xs: "auto", sm: "0" },
                }}
              >
                <WorkspacePremiumIcon sx={{ fontSize: 36, color: darkBg }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight={600}
                mb={1}
                textAlign={{ xs: "center", sm: "left" }}
              >
                End-to-End Solutions
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign={{ xs: "center", sm: "left" }}
              >
                We manage every aspect of your furnishing project, saving you
                time and resources.
              </Typography>
            </Box>

            {/* Card 2 */}
            <Box
              sx={{
                flex: 1,
                background: "linear-gradient(135deg, #fff7f0, #f2e6d9)",
                borderRadius: "20px",
                p: { xs: 3, sm: 3.5 },
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  backgroundColor: gold,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  mx: { xs: "auto", sm: "0" },
                }}
              >
                <SupportAgentIcon sx={{ fontSize: 36, color: darkBg }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight={600}
                mb={1}
                textAlign={{ xs: "center", sm: "left" }}
              >
                After Sales Support
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign={{ xs: "center", sm: "left" }}
              >
                We are committed to providing ongoing support & after-sales
                services with a focus on your satisfaction.
              </Typography>
            </Box>
          </Box>

          {/* Bottom Image Card */}
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              backgroundImage:
                "linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.7)),url('/images/interior-c.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: { xs: 220, sm: 260, md: 280 },
              borderRadius: "20px",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                color: "#fff",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Superior Quality
              </Typography>
              <Typography variant="body2">
                Our partnership with the best suppliers guarantees
                <br />
                fine materials and craftsmanship.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Section - Large Card */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "1 1 40%" },
            backgroundImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.5)),url('/images/interior-e.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "#fff",
            borderRadius: "20px",
            p: { xs: 3, sm: 4 },
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            height: { xs: 480, sm: "auto" },
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={1}>
            Bespoke Furniture
          </Typography>
          <Typography variant="body1">
            We specialize in sourcing custom-made furniture and décor pieces that
            perfectly match your unique vision and design requirements.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InteriorSection;
